import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import NavBar from '../components/navBar';
import NativeAffiliateBanner from '../components/ads/NativeAffiliateBanner';
import useApi from '../hooks/useApi';
import usePageSeo from '../utils/usePageSeo';
import {
  getBlogsPageCache,
  getLastBlogsListPage,
  loadBlogsPage,
  setLastBlogsListPage,
} from '../utils/blogsCache';

function viewToSearch(view) {
  if (!view || view === 'home' || view === 'dashboard') return '';
  return `?view=${encodeURIComponent(view)}`;
}

function isTokenValid(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
}

function pageFromSearchParams(searchParams) {
  const raw = Number(searchParams.get('page'));
  if (Number.isFinite(raw) && raw >= 1) {
    return Math.floor(raw);
  }
  return getLastBlogsListPage();
}

export default function BlogsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { request } = useApi();
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const initialPage = pageFromSearchParams(searchParams);
  const cached = getBlogsPageCache(initialPage);
  const [page, setPage] = useState(initialPage);
  const [blogs, setBlogs] = useState(cached?.blogs || []);
  const [totalPages, setTotalPages] = useState(cached?.totalPages || 1);
  const [totalCount, setTotalCount] = useState(cached?.count || 0);
  const [loading, setLoading] = useState(!cached);

  usePageSeo({
    title: 'Blogs',
    description: 'Reading guides, book summaries, and classic literature insights from Poppyruz.',
    path: '/blogs',
  });

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    setIsAuthenticated(Boolean(token && isTokenValid(token)));
  }, []);

  useEffect(() => {
    const urlHasPage = searchParams.has('page');
    if (!urlHasPage && page > 1) {
      setSearchParams({ page: String(page) }, { replace: true });
    }
  }, [page, searchParams, setSearchParams]);

  const handleNavigate = useCallback(
    (view) => {
      navigate({ pathname: '/', search: viewToSearch(view) });
    },
    [navigate],
  );

  const handleLogout = useCallback(async () => {
    try {
      await request({
        method: 'post',
        url: '/api/v1/users/logout/',
        withAuth: true,
        data: { refresh: localStorage.getItem('refresh_token') },
      });
    } catch (err) {
      console.error('Logout API error:', err);
    }
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_id');
    localStorage.removeItem('name');
    setIsAuthenticated(false);
    handleNavigate('home');
  }, [request, handleNavigate]);

  useEffect(() => {
    const urlPage = pageFromSearchParams(searchParams);
    if (urlPage !== page) {
      setPage(urlPage);
    }
    // Only sync when the URL page param changes from outside (e.g. back link).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const goToPage = useCallback(
    (nextPage) => {
      const safe = Math.max(1, nextPage);
      setLastBlogsListPage(safe);
      setPage(safe);
      setSearchParams(safe <= 1 ? {} : { page: String(safe) }, { replace: true });
    },
    [setSearchParams],
  );

  useEffect(() => {
    let active = true;
    setLastBlogsListPage(page);

    const cachedPage = getBlogsPageCache(page);
    if (cachedPage) {
      setBlogs(cachedPage.blogs);
      setTotalPages(cachedPage.totalPages);
      setTotalCount(cachedPage.count);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const load = async () => {
      try {
        const result = await loadBlogsPage(page);
        if (!active) return;
        setBlogs(result.blogs);
        setTotalPages(result.totalPages);
        setTotalCount(result.count);
      } catch (err) {
        console.error('Failed to load blogs', err);
        if (active) setBlogs([]);
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [page]);

  const canPrev = page > 1;
  const canNext = page < totalPages;

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <NavBar
        isAuthenticated={isAuthenticated}
        currentView="blogs"
        onNavigate={handleNavigate}
        onLogout={handleLogout}
      />
      <main className="mx-auto max-w-5xl px-4 pb-16 pt-24">
        <h1 className="mb-8 text-4xl font-bold">Blogs</h1>

        {loading ? (
          <div className="py-16 text-center text-gray-500">Loading blogs...</div>
        ) : blogs.length === 0 ? (
          <div className="rounded-lg border p-8 text-center text-gray-500">No blogs available yet.</div>
        ) : (
          <>
            <div className="grid gap-4">
              {blogs.map((blog) => (
                <Link
                  to={`/blogs/${blog.slug}`}
                  state={{ fromPage: page }}
                  key={blog.slug}
                  className="block rounded-lg border p-5 transition hover:border-violet-400 hover:bg-violet-50/30"
                >
                  <h2 className="text-xl font-semibold">{blog.title}</h2>
                  <p className="mt-2 line-clamp-2 text-sm text-gray-600">{blog.excerpt || 'Read article'}</p>
                </Link>
              ))}
            </div>

            {totalPages > 1 ? (
              <div className="mt-8 flex items-center justify-between border-t pt-6">
                <button
                  type="button"
                  disabled={!canPrev}
                  onClick={() => goToPage(page - 1)}
                  className="rounded-lg border px-4 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-40"
                >
                  ← Previous
                </button>
                <p className="text-sm text-gray-600">
                  Page {page} of {totalPages}
                  {totalCount ? ` · ${totalCount} articles` : ''}
                </p>
                <button
                  type="button"
                  disabled={!canNext}
                  onClick={() => goToPage(page + 1)}
                  className="rounded-lg border px-4 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Next →
                </button>
              </div>
            ) : null}
          </>
        )}
        <NativeAffiliateBanner placement="blogs" className="mt-12" />
      </main>
    </div>
  );
}
